import {
  BorrowerEntity,
  LoanApplicationBorrower,
  LoanApplicationBorrowers,
  OwnedRealEstate,
  Tradeline,
} from "@eave/shared/types/api";
import * as _ from "underscore";
import moment from "moment";
import BorrowerEntityUtil from "@eave/shared/utils/borrowers";

type PresentHousingExpenses = {
  address: string;
  owned_real_estate?: OwnedRealEstate;
  associated_borrowers: LoanApplicationBorrower[];
};

type BorrowerTradelines = Map<BorrowerEntity, Tradeline[]>;

const CreditSummaryTools = {
  /**
   * returns a map borrower current addresses as the key, and the borrowers that have that current address
   * as the value
   */
  getMapOfPresentAddresses(
    borrowers: LoanApplicationBorrowers,
  ): Map<string, LoanApplicationBorrower[]> {
    const addresses = new Map<string, LoanApplicationBorrower[]>();
    Object.values(borrowers).forEach(b => {
      if (b.confirmed_at) {
        const key = `${b.current_address_line1}, ${b.current_city}, ${b.current_state}`;
        if (addresses.size === 0 || !addresses.has(key)) {
          addresses.set(key, [b]);
        } else {
          const borrowerList = addresses.get(key);
          addresses.set(key, [...borrowerList, b]);
        }
      }
    });
    return addresses;
  },

  /**
   * getOwnedresidenceByAddress returns the OwnedRealEstate matching the address given or a null value
   * @param addr
   * @param ownedRealEstate
   */
  getOwnedResidenceByAddress(
    addr: string,
    ownedRealEstate: OwnedRealEstate[],
  ): OwnedRealEstate | null {
    let ore: OwnedRealEstate | null = null;
    ownedRealEstate.forEach(property => {
      const propertyAddress = `${property.address_line1}, ${property.city}, ${property.state}`;
      const propertyOperatorAddress = `${property.operator_address_line1}, ${property.operator_city}, ${property.operator_state}`;
      if (propertyAddress === addr || propertyOperatorAddress === addr) {
        ore = property;
      }
    });
    return ore;
  },

  /**
   * getPresentHousingExpenses returns an array of the type `PresentHousingExpenses` which provides the
   * data needed to populate the Present Housing Expenses table in the CreditSummaryHurdle
   * @param borrowers
   * @param ownedRealEstate
   */
  getPresentHousingExpenses(
    borrowers: LoanApplicationBorrowers,
    ownedRealEstate: OwnedRealEstate[],
  ): PresentHousingExpenses[] {
    let presentHousingExpenses: PresentHousingExpenses[] = [];
    const presentAddressMap = this.getMapOfPresentAddresses(borrowers);

    presentAddressMap.forEach((associatedBorrowers: LoanApplicationBorrower[], addr: string) => {
      const presentHousingExpense: PresentHousingExpenses = {
        address: addr,
        associated_borrowers: associatedBorrowers,
      };
      const ore = this.getOwnedResidenceByAddress(addr, ownedRealEstate);
      if (ore !== null) {
        presentHousingExpense.owned_real_estate = ore;
      }
      presentHousingExpenses = [...presentHousingExpenses, presentHousingExpense];
    });
    return presentHousingExpenses;
  },

  /**
   * getTradelinesByBorrower takes an array of Tradelines, groups them by the borrower entity,
   * and sorts them by opened_date on the Tradeline. It returns a nested array of Tradelines, ordered by borrower
   * entity from lowest to highest, or an empty array if they're not present
   * e.g. `[Borrower1SortedTradelines, Borrower2SortedTradelines, Borrower3SortedTradelines, Borrower4SortedTradelines]`
   * @param tradelines
   */
  getTradelinesByBorrower(tradelines: Tradeline[]) {
    const tradelinesByBorrower = this.groupTradelinesByBorrowerEntity(tradelines);

    const b1 = tradelinesByBorrower.get(BorrowerEntity.Borrower1);
    const b2 = tradelinesByBorrower.get(BorrowerEntity.Borrower2);
    const b3 = tradelinesByBorrower.get(BorrowerEntity.Borrower3);
    const b4 = tradelinesByBorrower.get(BorrowerEntity.Borrower4);

    const sortedB1 = b1 ? this.sortTradelinesByOpenedDate(b1) : [];
    const sortedB2 = b2 ? this.sortTradelinesByOpenedDate(b2) : [];
    const sortedB3 = b3 ? this.sortTradelinesByOpenedDate(b3) : [];
    const sortedB4 = b4 ? this.sortTradelinesByOpenedDate(b4) : [];
    return [sortedB1, sortedB2, sortedB3, sortedB4];
  },

  sortTradelinesByOpenedDate(tls: Tradeline[]): Tradeline[] {
    return _.sortBy(tls, (t: Tradeline) => moment(t.opened_date));
  },

  /**
   * setOrAppendTradeline handles setting or appending the tradeline to a BorrowerTradelines map
   * @param tr
   * @param bt
   */
  setOrAppendTradeline(tr: Tradeline, bt: BorrowerTradelines, ent: BorrowerEntity) {
    if (bt.has(ent)) {
      let tls = bt.get(ent);
      tls = [...tls, tr];
      bt.set(ent, tls);
    } else {
      bt.set(ent, [tr]);
    }
  },

  /**
   * groupTradelinesByBorrower will create a map wherein the key is the one of the 4 borrower entity values,
   * and the tradelines are associated to them.
   *
   * In the event that there are entities that contain multiple borrowers, it will default to the lowest number borrower.
   * e.g.
   *  * BorrowerEntity.Borrower1.with(BorrowerEntity.Borrower2) will be assigned to Borrower1
   *  * BorrowerEntity.Borrower2.With(BorrowerEntity.Borrower3) will be assigned to Borrower2
   *  * BorrowerEntity.Borrower1.With(BorrowerEntity.Borrower4) will be assigned to Borrower1
   * @param tradelines
   */
  groupTradelinesByBorrowerEntity(tradelines: Tradeline[]): BorrowerTradelines {
    const borrowerTradelines: BorrowerTradelines = new Map<BorrowerEntity, Tradeline[]>();
    tradelines.forEach(tr => {
      const entityUtil = new BorrowerEntityUtil(tr.borrower_entity);
      if (entityUtil.has(BorrowerEntity.Borrower1)) {
        this.setOrAppendTradeline(tr, borrowerTradelines, BorrowerEntity.Borrower1);
      } else if (entityUtil.has(BorrowerEntity.Borrower2)) {
        this.setOrAppendTradeline(tr, borrowerTradelines, BorrowerEntity.Borrower2);
      } else if (entityUtil.has(BorrowerEntity.Borrower3)) {
        this.setOrAppendTradeline(tr, borrowerTradelines, BorrowerEntity.Borrower3);
      } else if (entityUtil.has(BorrowerEntity.Borrower4)) {
        this.setOrAppendTradeline(tr, borrowerTradelines, BorrowerEntity.Borrower4);
      }
    });
    return borrowerTradelines;
  },
};

export default CreditSummaryTools;
